import { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Loader2, Send, EyeOff, Eye, ThumbsUp, CornerDownRight, Settings, Trash2 } from "lucide-react";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function Community() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [posts, setPosts] = useState(null);
  const [webinars, setWebinars] = useState([]);
  const [filter, setFilter] = useState("");
  const [body, setBody] = useState("");
  const [expId, setExpId] = useState("");
  const [posting, setPosting] = useState(false);

  const load = useCallback(() => {
    const qs = filter ? `?experience_id=${filter}` : "";
    api.get(`/admin/community${qs}`).then((r) => setPosts(r.data)).catch(() => setPosts([]));
  }, [filter]);
  useEffect(() => {
    load();
    api.get("/admin/webinars").then((r) => setWebinars(r.data)).catch(() => {});
  }, [load]);

  const createPost = async () => {
    if (!body.trim()) return;
    setPosting(true);
    try {
      await api.post("/admin/community", { body: body.trim(), experience_id: expId || undefined });
      setBody(""); setExpId(""); load();
      toast.success("Posted to the community");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    setPosting(false);
  };
  const reply = async (postId, text) => {
    try { await api.post("/admin/community", { body: text, parent_id: postId }); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const resonate = async (post) => {
    try {
      const { data } = await api.post(`/admin/community/${post.id}/resonate`);
      setPosts((ps) => ps.map((p) => {
        if (p.id === post.id) return { ...p, resonates: data.resonates, you_resonated: data.you_resonated };
        return { ...p, replies: (p.replies || []).map((r) => r.id === post.id ? { ...r, resonates: data.resonates, you_resonated: data.you_resonated } : r) };
      }));
    } catch { /* ignore */ }
  };
  const toggleHide = async (post) => {
    try { await api.post(`/admin/community/${post.id}/hide`, { hidden: !post.hidden }); load(); toast.success(post.hidden ? "Post restored" : "Post hidden"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };
  const del = async (post) => {
    if (!window.confirm("Permanently delete this post (and its replies)?")) return;
    try { await api.delete(`/admin/community/${post.id}`); load(); toast.success("Post deleted"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="p-8 max-w-3xl" data-testid="community-admin-page">
      <h1 className="text-3xl font-black tracking-tighter mb-1">Community wall</h1>
      <p className="text-[#4B5563] mb-6">Reflections from your members. {isAdmin ? "You can post, reply, hide anything, and tune the AI conversation starters." : "Post, reply, and hide anything on your own experiences."}</p>

      {isAdmin && <AiSettings />}

      <div className="flex items-center gap-2 mb-4">
        <span className="text-sm font-semibold text-[#4B5563]">Filter:</span>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="community-filter" className="border border-[#E5E7EB] px-3 py-2 text-sm">
          <option value="">All experiences</option>
          {webinars.map((w) => <option key={w.id} value={w.id}>{w.title}</option>)}
        </select>
      </div>

      <div className="bg-white border border-[#0A0A0A] p-5 mb-6">
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3} placeholder="Share a reflection or prompt to your community…" data-testid="community-admin-compose"
          className="w-full border border-[#E5E7EB] px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#6D28B5]" />
        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
          <select value={expId} onChange={(e) => setExpId(e.target.value)} data-testid="community-admin-exp" className="border border-[#E5E7EB] px-3 py-2 text-sm">
            <option value="">No specific experience</option>
            {webinars.map((w) => <option key={w.id} value={w.id}>{w.title}</option>)}
          </select>
          <button onClick={createPost} disabled={posting || !body.trim()} data-testid="community-admin-post-btn" className="bg-[#6D28B5] text-white hover:bg-[#4A1A7A] disabled:opacity-50 px-5 py-2.5 font-semibold flex items-center gap-2">
            {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Post
          </button>
        </div>
      </div>

      {posts === null ? (
        <div className="text-[#9CA3AF] flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : posts.length === 0 ? (
        <div className="bg-white border border-dashed border-[#9CA3AF] p-12 text-center text-[#6B7280]" data-testid="community-admin-empty">No reflections yet. The AI will gently start the conversation, or post one yourself.</div>
      ) : (
        <div className="space-y-4">{posts.map((p) => <AdminPost key={p.id} post={p} onReply={reply} onToggleHide={toggleHide} onResonate={resonate} onDelete={del} />)}</div>
      )}
    </div>
  );
}

function AdminReply({ post, onResonate, onDelete, canModerate }) {
  return (
    <div className="pl-4 border-l-2 border-[#F3F4F6]" data-testid={`admin-post-${post.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-bold text-sm">{post.author_name}</span>
        {(post.author_type === "host" || post.author_type === "admin") && <span className="text-[10px] uppercase tracking-wider bg-[#6D28B5] text-white px-1.5 py-0.5">Guide</span>}
        <span className="text-xs text-[#9CA3AF]">{timeAgo(post.created_at)}</span>
      </div>
      <p className="text-sm text-[#374151] mt-1 whitespace-pre-wrap">{post.body}</p>
      <div className="flex items-center gap-3 mt-1">
        <button onClick={() => onResonate(post)} data-testid={`admin-resonate-${post.id}`} className={`text-xs font-semibold flex items-center gap-1 ${post.you_resonated ? "text-[#6D28B5]" : "text-[#9CA3AF] hover:text-[#6D28B5]"}`}>
          <ThumbsUp className="w-3 h-3" /> {post.resonates} resonate{post.resonates === 1 ? "" : "s"}
        </button>
        {canModerate && (
          <button onClick={() => onDelete(post)} data-testid={`admin-delete-${post.id}`} className="text-xs font-semibold text-[#FF3B30] flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
        )}
      </div>
    </div>
  );
}

function AdminPost({ post, onReply, onToggleHide, onResonate, onDelete }) {
  const [text, setText] = useState("");
  const [open, setOpen] = useState(false);
  const submit = async () => { if (!text.trim()) return; await onReply(post.id, text.trim()); setText(""); setOpen(false); };
  return (
    <div className={`bg-white border border-[#0A0A0A] p-4 ${post.hidden ? "opacity-50" : ""}`} data-testid={`admin-post-${post.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-bold text-sm">{post.author_name}</span>
            {(post.author_type === "host" || post.author_type === "admin") && <span className="text-[10px] uppercase tracking-wider bg-[#6D28B5] text-white px-1.5 py-0.5">Guide</span>}
            <span className="text-xs text-[#9CA3AF]">{timeAgo(post.created_at)}</span>
            {post.hidden && <span className="text-[10px] uppercase tracking-wider bg-[#FEE2E2] text-[#B91C1C] px-1.5 py-0.5">Hidden</span>}
          </div>
          {post.experience_title && (
            post.experience_slug
              ? <a href={`/w/${post.experience_slug}`} target="_blank" rel="noreferrer" data-testid={`admin-post-exp-link-${post.id}`} className="text-xs text-[#16B6A4] hover:text-[#6D28B5] hover:underline mt-0.5 inline-block">on {post.experience_title}</a>
              : <div className="text-xs text-[#16B6A4] mt-0.5">on {post.experience_title}</div>
          )}
          <p className="text-sm text-[#374151] mt-1.5 whitespace-pre-wrap">{post.body}</p>
          <div className="flex items-center gap-3 mt-2">
            <button onClick={() => onResonate(post)} data-testid={`admin-resonate-${post.id}`} className={`text-xs font-semibold flex items-center gap-1 ${post.you_resonated ? "text-[#6D28B5]" : "text-[#9CA3AF] hover:text-[#6D28B5]"}`}>
              <ThumbsUp className="w-3 h-3" /> {post.resonates} resonate{post.resonates === 1 ? "" : "s"}
            </button>
            <button onClick={() => setOpen(!open)} className="text-xs font-semibold text-[#6D28B5] flex items-center gap-1" data-testid={`admin-reply-toggle-${post.id}`}><CornerDownRight className="w-3 h-3" /> Reply</button>
            {post.can_moderate && (
              <button onClick={() => onToggleHide(post)} className="text-xs font-semibold text-[#B45309] flex items-center gap-1" data-testid={`admin-hide-${post.id}`}>
                {post.hidden ? <><Eye className="w-3 h-3" /> Restore</> : <><EyeOff className="w-3 h-3" /> Hide</>}
              </button>
            )}
            {post.can_moderate && (
              <button onClick={() => onDelete(post)} className="text-xs font-semibold text-[#FF3B30] flex items-center gap-1" data-testid={`admin-delete-${post.id}`}>
                <Trash2 className="w-3 h-3" /> Delete
              </button>
            )}
          </div>
          {open && (
            <div className="flex gap-2 mt-2">
              <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a reply…" data-testid={`admin-reply-input-${post.id}`} className="flex-1 border border-[#E5E7EB] px-3 py-1.5 text-sm" />
              <button onClick={submit} className="bg-[#6D28B5] text-white px-3 text-sm font-semibold" data-testid={`admin-reply-submit-${post.id}`}><Send className="w-4 h-4" /></button>
            </div>
          )}
          {post.replies && post.replies.length > 0 && (
            <div className="mt-3 space-y-3">{post.replies.map((r) => <AdminReply key={r.id} post={r} onResonate={onResonate} onDelete={onDelete} canModerate={post.can_moderate} />)}</div>
          )}
        </div>
      </div>
    </div>
  );
}

function AiSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/community/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async (patch) => {
    const next = { ...s, ...patch };
    setS(next); setSaving(true);
    try { await api.put("/admin/community/settings", next); } catch { toast.error("Failed to save"); }
    setSaving(false);
  };
  if (!s) return null;
  return (
    <div className="bg-[#F9FAFB] border border-[#E5E7EB] p-4 mb-6" data-testid="community-ai-settings">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 font-semibold text-sm"><Settings className="w-4 h-4 text-[#6D28B5]" /> AI conversation starters {saving && <Loader2 className="w-3 h-3 animate-spin text-[#9CA3AF]" />}</div>
        <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer" data-testid="community-ai-toggle">
          <input type="checkbox" checked={s.community_ai_enabled} onChange={(e) => save({ community_ai_enabled: e.target.checked })} className="w-4 h-4 accent-[#6D28B5]" />
          {s.community_ai_enabled ? "On" : "Off"}
        </label>
      </div>
      <div className="flex items-center gap-3 mt-3 text-sm text-[#4B5563]">
        Post a new starter every
        <input type="number" min="1" value={s.community_min_days} onChange={(e) => save({ community_min_days: Number(e.target.value) })} className="w-16 border border-[#E5E7EB] px-2 py-1" data-testid="community-min-days" />
        to
        <input type="number" min="1" value={s.community_max_days} onChange={(e) => save({ community_max_days: Number(e.target.value) })} className="w-16 border border-[#E5E7EB] px-2 py-1" data-testid="community-max-days" />
        days. Each gets 3 thoughtful AI replies (at 20, 30 &amp; 60 min) and resonates that trickle in over hours.
      </div>
    </div>
  );
}
